import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_LABEL } from "constants/fileUploadLimits";
import { buildFlowDocument } from "utils/documentMetaUtils";

const MAX_FILE_SIZE = MAX_UPLOAD_FILE_SIZE_BYTES;

/**
 * Name a document and upload its file; `onAdd` receives the finished flow document. When `onAdd`
 * saves it (returns a promise), the dialog waits, and stays open with the file if that fails.
 */
const AddDocumentDialog = ({ open, onClose, onAdd, title = "Add document" }) => {
  const [{ token }] = useKothar();
  const [name, setName] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [fileLabel, setFileLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setFileUrl(null);
    setFileLabel("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const upload = useCallback(
    async (file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File is too large (max ${MAX_UPLOAD_FILE_SIZE_LABEL}).`);
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      setUploading(true);
      try {
        const res = await axios.post(`${API_URL}/file/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setFileUrl(res?.data?.url);
        setFileLabel(file.name);
        // A file picked before a name was typed names the document after itself.
        setName((current) => current || file.name.replace(/\.[^.]+$/, ""));
      } catch (err) {
        toast.error(err?.response?.data?.errorMessage || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [token]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => files[0] && upload(files[0]),
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    disabled: uploading,
  });

  const handleAdd = async () => {
    if (!name.trim()) {
      toast.error("Enter a document name.");
      return;
    }
    if (!fileUrl) {
      toast.error("Upload a file.");
      return;
    }
    setSaving(true);
    try {
      await onAdd(buildFlowDocument({ name: name.trim(), url: fileUrl }));
      close();
    } catch (err) {
      toast.error(err?.message || "Could not save the document. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
      <DialogContent className="space-y-3 pt-1">
        <TextField
          label="Document name"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          sx={{ mt: 1 }}
        />
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
            isDragActive ? "border-orange-400 bg-orange-50" : "border-app-border"
          }`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <CircularProgress size={28} />
          ) : (
            <>
              <FaCloudUploadAlt className="mx-auto text-2xl text-app-text-muted mb-1" />
              <Typography variant="caption">
                Click or drag file (max {MAX_UPLOAD_FILE_SIZE_LABEL})
              </Typography>
              {fileLabel && (
                <Typography variant="body2" className="mt-2 font-medium truncate">
                  {fileLabel}
                </Typography>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAdd} disabled={uploading || saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentDialog;
